import React, { useEffect, useMemo, useState } from 'react';

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};

const modalStyle = {
  backgroundColor: '#ffffff',
  width: '95%',
  maxWidth: '500px',
  maxHeight: '80vh',
  borderRadius: '14px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const inputStyle = {
  padding: '12px 14px',
  border: '1px solid #d1d5db',
  borderRadius: '10px',
  fontSize: '1em',
  outline: 'none',
  width: '100%',
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowY: 'auto',
};

const listItemStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
  cursor: 'pointer',
};

// SHOP-175: mirrors ProductDetails.jsx's reversed pairing for an expired
// item shown at regular_price (SHOP-147/153/172) - the stale item.price
// struck through, item.regular_price shown clean as the current price.
const regularPriceStrikeStyle = {
  fontSize: '0.85em',
  color: '#9ca3af',
  textDecoration: 'line-through',
};

const hasUsablePrice = (value) => value != null && value !== 0;

const ItemSearchModal = ({
  isOpen,
  onClose,
  items = [],
  selectedCategories,
  setSelectedCategories,
  selectedItemsMap,
  setSelectedItemsMap,
  translations,
  language,
  formatCurrency,
  onSelectItem
}) => {
  const [query, setQuery] = useState('');

  // Reset input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // 🔍 Live search
  const filteredItems = useMemo(() => {
  if (!query.trim()) return [];

  const q = query.toLowerCase();

  return items
    .filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.brand?.toLowerCase().includes(q) ||
      item.matched_category_es?.toLowerCase().includes(q) ||
      item.matched_category?.toLowerCase().includes(q) ||
      item.detail?.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      // 1. matched_category
      const catA = (a.matched_category || '').toLowerCase();
      const catB = (b.matched_category || '').toLowerCase();

      if (catA < catB) return -1;
      if (catA > catB) return 1;

      // 2. name
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();

      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      // 3. price
      const priceA = a.price ?? Infinity;
      const priceB = b.price ?? Infinity;

      return priceA - priceB;
    });

}, [query, items]);

    const [confirmData, setConfirmData] = useState(null);

  // ✅ Selection logic (CRITICAL)
    const handleSelectItem = (item) => {
      onSelectItem?.(item);
      onClose();
    };


  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 700 }}>
            {translations?.searchItems || 'Search items'}
          </h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.2em',
              cursor: 'pointer',
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* INPUT */}
        <div style={{ padding: '14px 16px' }}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              translations?.searchItemPlaceholder ||
              'Search by name, brand or description'
            }
            style={inputStyle}
          />
        </div>

        {/* RESULTS */}
        <ul style={listStyle}>
          {filteredItems.map((item) => (
            <li
              key={item.productid}
              style={{
                ...listItemStyle,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}
              onClick={() => handleSelectItem(item)}
            >
              {/* Nombre del item */}
              <div style={{ fontWeight: 600 }}>
                {item.name}
              </div>

              {/* Metadata */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.85em',
                  color: '#6b7280'
                }}
              >
                <span>
                  {language === 'es'
                    ? item.matched_category_es || item.matched_category
                    : item.matched_category}
                  {' · '}
                  {item.store}
                </span>

                {/* Precio */}
                {item.is_regular_price ? (
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    {hasUsablePrice(item.price) && (
                      <span style={regularPriceStrikeStyle}>
                        {formatCurrency ? formatCurrency(item.price) : `$${Number(item.price).toFixed(2)}`}
                      </span>
                    )}
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      {formatCurrency ? formatCurrency(item.regular_price) : `$${Number(item.regular_price).toFixed(2)}`}
                    </span>
                  </span>
                ) : (
                  item.price != null && (
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      {formatCurrency ? formatCurrency(item.price) : `$${Number(item.price).toFixed(2)}`}
                    </span>
                  )
                )}
              </div>
            </li>
          ))}

          {query && filteredItems.length === 0 && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.noItemsFound || 'No items found'}
            </li>
          )}
        </ul>

      </div>
    </div>
  );
};

export default ItemSearchModal;
